import { useState, useCallback, useRef } from 'react';
import { authenticatedFetch } from '../utils/authApi';

export interface SheetInfo {
  name: string;
  rows: number;
  columns: number;
  column_names?: string[];
}

export interface UploadProgress {
  fileId: string;
  filename: string;
  status: 'queued' | 'uploading' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  error?: string;
  message?: string;  // Progress message like "Processing page 5/100"
  // Excel multi-sheet support
  available_sheets?: SheetInfo[];
  sheet_name?: string;
}

export interface AsyncUploadResult {
  fileId: string;
  filename: string;
  success: boolean;
  error?: string;
}

export interface DuplicateCheckResult {
  has_duplicate: boolean;
  existing_files: {
    id: string;
    filename: string;
    filetype: string;
    upload_date: string;
    content_preview: string;
    content_length: number;
    processing_status: string;
    // Excel multi-sheet support
    available_sheets?: Array<{ name: string; rows: number; columns: number; column_names?: string[] }>;
  }[];
  checked_days: number;
}

export interface BatchDuplicateCheckResult {
  results: Record<string, {
    has_duplicate: boolean;
    existing_files: DuplicateCheckResult['existing_files'];
  }>;
  summary: {
    total_files: number;
    duplicates_found: number;
    new_files: number;
  };
  checked_days: number;
}

export interface ReusedDocument {
  id: string;
  filename: string;
  filetype: string;
  reused: boolean;
  original_upload_date: string;
  processing_status: string;
  has_content: boolean;
  // Excel multi-sheet support
  available_sheets?: Array<{ name: string; rows: number; columns: number; column_names?: string[] }>;
  sheet_name?: string;
}

export const useAsyncUpload = () => {
  const [uploads, setUploads] = useState<Map<string, UploadProgress>>(new Map());
  const pollIntervals = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const retryCounters = useRef<Map<string, number>>(new Map());  // Track consecutive failures
  const inProgressUploads = useRef<Set<string>>(new Set());  // Track files being processed (reliable for cancel check)

  const startPolling = useCallback((fileId: string) => {
    // Clear existing polling for this file
    const existingInterval = pollIntervals.current.get(fileId);
    if (existingInterval) {
      clearInterval(existingInterval);
    }
    retryCounters.current.set(fileId, 0);  // Reset retry counter

    const pollProgress = async () => {
      // Check if we should still be polling
      if (!pollIntervals.current.has(fileId)) {
        return;
      }
      
      try {
        const response = await authenticatedFetch(`/upload_status/${fileId}`);
        if (!response.ok) {
          console.warn(`[useAsyncUpload] Poll response not ok: ${response.status} for ${fileId}`);
          // Handle transient errors (401, 503) with retry
          if (response.status === 401 || response.status === 503) {
            const currentRetries = retryCounters.current.get(fileId) || 0;
            retryCounters.current.set(fileId, currentRetries + 1);
            
            // Allow up to 10 consecutive failures (20 seconds of retries)
            if (currentRetries < 10) {
              console.warn(`[useAsyncUpload] Transient ${response.status} for ${fileId}, retry ${currentRetries + 1}/10`);
              return;  // Keep polling, don't mark as failed yet
            }
          }
          throw new Error(`Failed to get upload status: ${response.status}`);
        }

        // Success - reset retry counter
        retryCounters.current.set(fileId, 0);
        const status = await response.json();
        
        setUploads(prev => {
          const current = prev.get(fileId);
          if (!current) return prev;
          
          const newMap = new Map(prev);
          newMap.set(fileId, {
            ...current,
            status: status.status,
            progress: status.progress || current.progress,
            error: status.error,
            message: status.message || current.message,  // Progress message (e.g., "Processing page 5/100")
            // Excel multi-sheet support
            available_sheets: status.available_sheets || current.available_sheets,
            sheet_name: status.sheet_name || current.sheet_name
          });
          return newMap;
        });

        // Stop polling if completed or failed
        if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
          const interval = pollIntervals.current.get(fileId);
          if (interval) {
            clearInterval(interval);
            pollIntervals.current.delete(fileId);
            retryCounters.current.delete(fileId);
          }
          // No longer in-progress, cancel shouldn't be called on removal
          inProgressUploads.current.delete(fileId);
        }

      } catch (error) {
        console.error('[useAsyncUpload] Progress polling error:', error);
        retryCounters.current.delete(fileId);
        setUploads(prev => {
          const current = prev.get(fileId);
          if (!current) return prev;
          
          const newMap = new Map(prev);
          newMap.set(fileId, {
            ...current,
            status: 'failed',
            error: 'Failed to track progress'
          });
          return newMap;
        });

        // Stop polling on error
        const interval = pollIntervals.current.get(fileId);
        if (interval) {
          clearInterval(interval);
          pollIntervals.current.delete(fileId);
          retryCounters.current.delete(fileId);
        }
      }
    };

    // Poll immediately, then every 2 seconds
    // IMPORTANT: Set the interval FIRST so the immediate poll doesn't think it was cleared
    const interval = setInterval(pollProgress, 2000);
    pollIntervals.current.set(fileId, interval);
    pollProgress();  // Now call the initial poll after interval is stored
  }, []);

  /**
   * Legacy upload - sends file bytes through Web App
   * Kept for fallback if SAS upload fails
   */
  const startUploadLegacy = useCallback(async (file: File): Promise<string | null> => {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    
    // Initialize upload progress
    setUploads(prev => new Map(prev).set(tempId, {
      fileId: tempId,
      filename: file.name,
      status: 'uploading',
      progress: 0
    }));

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authenticatedFetch('/fast_upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      const realFileId = result.file_id;

      // Mark as in-progress (for reliable cancel check)
      inProgressUploads.current.add(realFileId);

      // Update with real file ID and start polling
      setUploads(prev => {
        const newMap = new Map(prev);
        newMap.delete(tempId);
        newMap.set(realFileId, {
          fileId: realFileId,
          filename: file.name,
          status: result.status || 'queued', // Use the actual status from backend
          progress: 10
        });
        return newMap;
      });

      // Start polling for progress
      startPolling(realFileId);
      return realFileId;

    } catch (error) {
      console.error('Upload error:', error);
      setUploads(prev => new Map(prev).set(tempId, {
        fileId: tempId,
        filename: file.name,
        status: 'failed',
        progress: 0,
        error: error instanceof Error ? error.message : 'Upload failed'
      }));
      return null;
    }
  }, [startPolling]);

  /**
   * Main upload method - uses SAS direct-to-blob upload
   * Falls back to legacy if SAS fails
   */
  const startUpload = useCallback(async (file: File): Promise<string | null> => {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    
    // Initialize upload progress
    setUploads(prev => new Map(prev).set(tempId, {
      fileId: tempId,
      filename: file.name,
      status: 'uploading',
      progress: 0
    }));

    try {
      // Step 1: Get SAS URL from backend
      const sasResponse = await authenticatedFetch('/get-sas-upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          file_size: file.size,
          content_type: file.type || 'application/octet-stream'
        })
      });

      if (!sasResponse.ok) {
        // Fall back to legacy upload if SAS endpoint not available
        console.warn('[useAsyncUpload] SAS endpoint failed, falling back to legacy upload');
        setUploads(prev => {
          const newMap = new Map(prev);
          newMap.delete(tempId);
          return newMap;
        });
        return await startUploadLegacy(file);
      }

      const sasData = await sasResponse.json();
      const { file_id: fileId, sas_url: sasUrl, blob_container, blob_path } = sasData;

      // Mark as in-progress (for reliable cancel check)
      inProgressUploads.current.add(fileId);

      // Update progress with real file ID
      setUploads(prev => {
        const newMap = new Map(prev);
        newMap.delete(tempId);
        newMap.set(fileId, {
          fileId,
          filename: file.name,
          status: 'uploading',
          progress: 5
        });
        return newMap;
      });

      // Step 2: Upload directly to Azure Blob Storage via PUT
      const uploadResponse = await fetch(sasUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type || 'application/octet-stream'
        },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error(`Direct upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      // Update progress after direct upload
      setUploads(prev => {
        const current = prev.get(fileId);
        if (!current) return prev;
        const newMap = new Map(prev);
        newMap.set(fileId, { ...current, progress: 50 });
        return newMap;
      });

      // Step 3: Notify backend that upload is complete
      const completeResponse = await authenticatedFetch('/complete-sas-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_id: fileId,
          filename: file.name,
          file_size: file.size,
          blob_container,
          blob_path
        })
      });

      if (!completeResponse.ok) {
        const errorData = await completeResponse.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to complete upload: ${completeResponse.statusText}`);
      }

      const result = await completeResponse.json();

      // Update with queued status and start polling
      setUploads(prev => {
        const newMap = new Map(prev);
        newMap.set(fileId, {
          fileId,
          filename: file.name,
          status: result.status || 'queued',
          progress: 10
        });
        return newMap;
      });

      // Start polling for processing progress
      startPolling(fileId);
      return fileId;

    } catch (error) {
      console.error('SAS upload error:', error);
      setUploads(prev => new Map(prev).set(tempId, {
        fileId: tempId,
        filename: file.name,
        status: 'failed',
        progress: 0,
        error: error instanceof Error ? error.message : 'Upload failed'
      }));
      return null;
    }
  }, [startPolling, startUploadLegacy]);

  const removeUpload = useCallback((fileId: string) => {
    // Clear polling
    const interval = pollIntervals.current.get(fileId);
    if (interval) {
      clearInterval(interval);
      pollIntervals.current.delete(fileId);
    }
    
    // Clean up in-progress tracking
    inProgressUploads.current.delete(fileId);

    // Always call cancel - backend handles gracefully if file is done
    authenticatedFetch(`/cancel-upload/${fileId}`, { method: 'POST' })
      .catch(() => {}); // Ignore errors - fire and forget

    // Remove from uploads
    setUploads(prev => {
      const newMap = new Map(prev);
      newMap.delete(fileId);
      return newMap;
    });
  }, []);

  /**
   * Cancel an in-progress upload - signals backend to stop processing
   * This should be called BEFORE deleteExistingUpload to stop background processing
   */
  const cancelUpload = useCallback(async (fileId: string): Promise<boolean> => {
    try {
      // First stop local polling
      const interval = pollIntervals.current.get(fileId);
      if (interval) {
        clearInterval(interval);
        pollIntervals.current.delete(fileId);
      }
      
      // Signal backend to cancel processing
      const response = await authenticatedFetch(`/cancel-upload/${fileId}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        console.warn(`[useAsyncUpload] Cancel response: ${response.status}`);
        return false;
      }
      
      const result = await response.json();
      
      // Update local state to cancelled
      setUploads(prev => {
        const current = prev.get(fileId);
        if (!current) return prev;
        
        const newMap = new Map(prev);
        newMap.set(fileId, { ...current, status: 'cancelled' });
        return newMap;
      });
      
      return result.success === true;
    } catch (error) {
      console.error('[useAsyncUpload] Cancel error:', error);
      return false;
    }
  }, []);

  const getUploadProgress = useCallback((fileId: string): UploadProgress | undefined => {
    return uploads.get(fileId);
  }, [uploads]);

  const getAllUploads = useCallback((): UploadProgress[] => {
    return Array.from(uploads.values());
  }, [uploads]);

  // Cleanup on unmount
  const cleanup = useCallback(() => {
    pollIntervals.current.forEach((interval) => {
      clearInterval(interval);
    });
    pollIntervals.current.clear();
  }, []);

  /**
   * Check for duplicate files before uploading
   * Returns files that have been uploaded by this user in the past N days
   */
  const checkDuplicates = useCallback(async (
    filenames: string[],
    days: number = 3
  ): Promise<BatchDuplicateCheckResult | null> => {
    try {
      const response = await authenticatedFetch('/check-duplicates-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filenames, days })
      });

      if (!response.ok) {
        console.error('Duplicate check failed:', response.statusText);
        return null;
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Duplicate check error:', error);
      return null;
    }
  }, []);

  /**
   * Reuse an existing document instead of re-uploading
   * Returns the document info needed to attach to conversation
   */
  const reuseDocument = useCallback(async (
    fileId: string
  ): Promise<ReusedDocument | null> => {
    try {
      const response = await authenticatedFetch('/reuse-document', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_id: fileId })
      });

      if (!response.ok) {
        console.error('Reuse document failed:', response.statusText);
        return null;
      }

      const result = await response.json();
      return result.document;
    } catch (error) {
      console.error('Reuse document error:', error);
      return null;
    }
  }, []);

  /**
   * Get user's recently uploaded documents
   */
  const getRecentDocuments = useCallback(async (
    days: number = 7,
    limit: number = 20
  ): Promise<ReusedDocument[] | null> => {
    try {
      const response = await authenticatedFetch(
        `/user-recent-documents?days=${days}&limit=${limit}`
      );

      if (!response.ok) {
        console.error('Get recent documents failed:', response.statusText);
        return null;
      }

      const result = await response.json();
      return result.documents;
    } catch (error) {
      console.error('Get recent documents error:', error);
      return null;
    }
  }, []);

  /**
   * Delete an existing uploaded file before re-uploading
   * Removes from Cosmos DB, Search Index, and Blob Storage
   */
  const deleteExistingUpload = useCallback(async (
    fileId: string
  ): Promise<boolean> => {
    try {
      const response = await authenticatedFetch(`/delete-upload/${fileId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        console.error(`[useAsyncUpload] Delete failed: ${response.status}`);
        return false;
      }

      const result = await response.json();
      return result.success === true;
    } catch (error) {
      console.error('[useAsyncUpload] Delete error:', error);
      return false;
    }
  }, []);

  /**
   * Start upload for Excel file with specific sheet selection
   * Uses /excel/upload_with_sheets endpoint for selective processing
   */
  const startUploadWithSheets = useCallback(async (
    file: File, 
    selectedSheets: string[]
  ): Promise<string | null> => {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    
    // Initialize upload progress
    setUploads(prev => new Map(prev).set(tempId, {
      fileId: tempId,
      filename: file.name,
      status: 'uploading',
      progress: 0
    }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('selected_sheets', JSON.stringify(selectedSheets));

      const response = await authenticatedFetch('/excel/upload_with_sheets', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      const realFileId = result.file_id;

      // Mark as in-progress (for reliable cancel check)
      inProgressUploads.current.add(realFileId);

      // Update with real file ID and start polling
      setUploads(prev => {
        const newMap = new Map(prev);
        newMap.delete(tempId);
        newMap.set(realFileId, {
          fileId: realFileId,
          filename: file.name,
          status: result.status || 'queued',
          progress: 10
        });
        return newMap;
      });

      // Start polling for progress
      startPolling(realFileId);
      
      return realFileId;

    } catch (error) {
      console.error('Excel upload with sheets failed:', error);
      setUploads(prev => {
        const newMap = new Map(prev);
        newMap.set(tempId, {
          fileId: tempId,
          filename: file.name,
          status: 'failed',
          progress: 0,
          error: error instanceof Error ? error.message : 'Upload failed'
        });
        return newMap;
      });
      return null;
    }
  }, [startPolling]);

  /**
   * Check if a file is an Excel file
   */
  const isExcelFile = useCallback((filename: string): boolean => {
    const lowerName = filename.toLowerCase();
    return lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls') || 
           lowerName.endsWith('.xlsm') || lowerName.endsWith('.xlsb');
  }, []);

  return {
    startUpload,       // Uses SAS direct-to-blob by default
    startUploadLegacy, // Fallback through Web App (if needed)
    startUploadWithSheets,
    isExcelFile,
    removeUpload,
    cancelUpload,      // Signal backend to stop processing
    getUploadProgress,
    getAllUploads,
    cleanup,
    // Duplicate detection
    checkDuplicates,
    reuseDocument,
    getRecentDocuments,
    deleteExistingUpload
  };
};